import React, { useState, useEffect } from "react";
import { Row, Col, Form } from "react-bootstrap";
import * as actions from "Store/Course/Instructor/OtherLabs/Actions";
import Options from "../Options";
import parse from "html-react-parser";
import "../../css/Numeric.scss";
import AnswerExplanation from "../../GradeSubmission/AnswerExplaination";
import { removeRemarks } from "../../GradeSubmission/GradeCheck";
import log from "loglevel";

function Numeric({ courseId, question, index, previousResponse, updateResponse, disable, Eval, showMarksToStudent, revealAnswers }) {
  const [responseId, setResponseId] = useState(null);
  const [saveResponseTimer, setSaveResponseTimer] = useState(null);
  const [userAnswer, setUserAnswer] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const mode = localStorage.getItem("mode");
  let minAnswerRange, maxAnswerRange, isAnswerCorrect = false, currentAnswer;

  const roundToPrecision = (num, precision = 16) => {
    return Math.round(num * Math.pow(10, precision)) / Math.pow(10, precision);
  };

  const parseAnswerRange = (answerString) => {
    let minAnswerRange, maxAnswerRange;

    try {
      const rangeArray = JSON.parse(answerString.replace(/'/g, '"'));
      if (Array.isArray(rangeArray)) {
        minAnswerRange = roundToPrecision(parseFloat(rangeArray[0]));
        maxAnswerRange = roundToPrecision(parseFloat(rangeArray[1]));
      } else {
        // Handle case where it's a single value like "5.0"
        minAnswerRange = maxAnswerRange = roundToPrecision(parseFloat(rangeArray));
      }
    } catch (e) {
      // Fallback for cases where answerString is just a number or other invalid format
      minAnswerRange = maxAnswerRange = roundToPrecision(parseFloat(answerString));
    }
    return [minAnswerRange, maxAnswerRange];
  };

  if (question.questionanswer_set) {
    [minAnswerRange, maxAnswerRange] = parseAnswerRange(question.questionanswer_set[0]?.answer);
    // log.log({ minAnswerRange, maxAnswerRange, question, previousResponse })

    if (previousResponse.response_text) {
      currentAnswer = roundToPrecision(parseFloat(previousResponse.response_text));
      if (currentAnswer >= minAnswerRange && currentAnswer <= maxAnswerRange) {
        isAnswerCorrect = true;
      }
    }
    // log.log({ isAnswerCorrect, Eval })
  }

  useEffect(() => {
    if (previousResponse) {
      // Set responseId if we don't have one yet
      if (responseId === null && previousResponse.id) {
        setResponseId(previousResponse.id);
      }

      // Only update userAnswer on initial load (when userAnswer is empty)
      // Don't update if user is typing - this prevents decimal point disappearing
      if (previousResponse.response_text && userAnswer === "") {
        setUserAnswer(previousResponse.response_text);
      }
    }
  }, [previousResponse, responseId]);

  const storeUserResponse = (value) => {
    // Per-question API call removed — local state only. Autosave runs every
    // 30s in student/index.jsx and on Submit Quiz, batching all answers into
    // one POST .../student_submission/?autosave=true.
    updateResponse({
      ...(previousResponse || {}),
      id: question.id,
      response_text: value,
    });
  };

  const handleInputChange = (event) => {
    let { value } = event.target;

    const numericPattern = /^[-+]?[0-9]*\.?[0-9]*([eE][-+]?[0-9]*)?$/;
    if (value !== "" && !numericPattern.test(value)) {
      return;
    }

    clearTimeout(saveResponseTimer);

    // Validate decimal places (max 16 digits after decimal point)
    // Handle both regular decimal notation and scientific notation
    if (value && !value.toLowerCase().includes('e')) {
      // Regular decimal notation
      if (value.includes('.')) {
        const parts = value.split('.');
        if (parts[1] && parts[1].length > 16) {
          // Truncate to 16 decimal places
          value = parts[0] + '.' + parts[1].substring(0, 16);
        }
      }
    } else if (value && value.toLowerCase().includes('e')) {
      // Scientific notation - convert to decimal and check precision
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        const decimalStr = numValue.toFixed(16);
        const trimmed = decimalStr.replace(/\.?0+$/, ''); // Remove trailing zeros
        if (trimmed.includes('.')) {
          const parts = trimmed.split('.');
          if (parts[1] && parts[1].length > 16) {
            value = numValue.toFixed(16);
          }
        }
      }
    }

    setUserAnswer(value);
    storeUserResponse(value);
  };

  const handleBlur = () => {
    // Save immediately when user leaves the input field
    // This ensures data is saved before submission
    if (saveResponseTimer) {
      clearTimeout(saveResponseTimer);
      setSaveResponseTimer(null);
    }
    if (userAnswer && !isSaving) {
      storeUserResponse(userAnswer);
    }
  };

  const saveScoredMarks = async (marks) => {
    await actions.addScoredMarksRequest(question, responseId, marks);
    updateResponse();
  };

  const renderQuestionText = () => {
    const sanitizedText = removeRemarks(question?.question_text);
    return <Col xs={12} className="question-text">{parse(sanitizedText)}</Col>;
  };

  const renderAnswerInput = () => {
    const showValidationBorder = Eval && !revealAnswers;
    const borderStyle = showValidationBorder ? { border: isAnswerCorrect ? '2px solid green' : '2px solid red', backgroundColor: isAnswerCorrect ? '#b9eddd' : '' } : {};
    return (
      <Col xs={12} className={`option ${disable ? "disabled" : ""}`}>
        <Options
          questionId={question.id}
          options={question.questionoption_set}
          questionType={question.questionType}
          disable={disable}
          Eval={Eval}
        />
        <div style={borderStyle}>
          <Form.Control
            type="text"
            inputMode="decimal"
            value={userAnswer}
            onChange={handleInputChange}
            onBlur={handleBlur}
            disabled={disable}
          />
        </div>
      </Col>
    );
  };

  const cleanAnswerText = (text) => {
    if (!text) return '';
    let cleaned = text.trim();
    if (cleaned.startsWith('[') && cleaned.endsWith(']')) cleaned = cleaned.slice(1, -1);
    if (cleaned.startsWith("'") && cleaned.endsWith("'")) cleaned = cleaned.slice(1, -1);
    if (cleaned.startsWith('"') && cleaned.endsWith('"')) cleaned = cleaned.slice(1, -1);
    cleaned = cleaned.replace(/\(\?[ib]\)/g, '');
    return cleaned.trim();
  };

  const getCorrectAnswerText = (questionanswer_set) => {
    if(!questionanswer_set || questionanswer_set.length==0) return "";
    return questionanswer_set.map(ans => {
      let raw = String(ans['answer'] || '').trim();
      if (raw.startsWith('[') && raw.endsWith(']')) {
        return raw.slice(1, -1).split(',').map(p => cleanAnswerText(p)).join(' / ');
      }
      return cleanAnswerText(raw);
    }).join(', ');
  };

  const renderEvaluationSection = () => {
    const showMarks = disable && showMarksToStudent !== false;
    const showAnswerOnly = revealAnswers && (Eval || mode === "I");

    if (!showMarks && !showAnswerOnly) return null;

    return (
      <>
        <Col xs={6}>

        </Col>
        <Col xs={6}>
          {showMarks && (
            <Form.Check>
              <Form.Label>Scored Marks</Form.Label>
              <Form.Control
                type="number"
                min="0"
                value={question?.questionresponse_set?.scored_marks || 0}
                step="0.1"
                onBlur={(e) => saveScoredMarks(e.target.value)}
                disabled
              />
            </Form.Check>
          )}

          {(Eval || mode === "I") && (
            <div style={{ marginRight: '-100px', marginTop: showMarks ? '-40px' : '0px', marginLeft: '-450px' }}>
              <AnswerExplanation explanation={question?.answer_explaination} correct_answer_text={getCorrectAnswerText(question?.questionanswer_set)} />
            </div>
          )}
        </Col>
      </>
    );
  };

  return (
    <div className="numeric-component" style={{ margin: 10 }}>
      <Row>
        <Col xs={12} style={{ marginBottom: 10 }}>
          <div className="question-header d-flex justify-content-between align-items-center">
            <b><i><span style={{ fontSize: 20, color: 'rgb(41, 121, 255)' }}>{`Question ${index + 1}`}</span></i></b>
            <span className="question-meta text-primary">
              <b><i>Question Type: {question.question_type === 'N' ? 'Numerical' : ''} <span style={{ marginLeft: '40px' }}>Marks: {question.marks}{question.negative_marks ? `, Negative: ${question.negative_marks % 1 === 0 ? Math.floor(question.negative_marks) : question.negative_marks}` : ''}</span></i></b>
            </span>
          </div>
        </Col>
        {renderQuestionText()}
        {renderAnswerInput()}
        {renderEvaluationSection()}
      </Row>
    </div>
  );
}

export default Numeric;
